import React, { FC, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';

// components
import BasicButton from '../../../components/buttons/basicButton';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';
import { FormulaType } from '../../../constants/formulaType';
// constants
import routes from '../../../constants/routes';

// helpers
import { formatNumberFull } from '../../../helpers/numeric';

import useRoleFlags from '../../../hooks/auth/useRoleFlags';
// hooks
import useFormulasCatalogQuery from '../../../hooks/formulas/useFormulasCatalogQuery';
import useGetLocationState from '../../../hooks/helper/useGetLocationState';

// models
import {
    FormulaCategory,
    FormulaTypesData,
} from '../../../models/formula.model';

import MessagesProgressBar from './MessagesProgressBar';

interface ChannelInfo {
    channel: string;
    percentage: number;
    color: string;
}
interface LiveMessagesProps {
    heading: string;
    disableFooter?: boolean;
    tableData: any;
    calculatePercentages: () => ChannelInfo[];
    totalMessages: number;
}

const LiveMessagesMain: FC<React.PropsWithChildren<LiveMessagesProps>> = ({
    heading,
    tableData,
    totalMessages,
    calculatePercentages,
}) => {
    const { hasCreateFormulaAccess } = useRoleFlags();
    const history = useHistory();
    const { getLocationState } = useGetLocationState('Back to live messages');
    const { data } = useFormulasCatalogQuery(FormulaCategory.Live);
    const activeChannelsWithZeroCount = Object.keys(FormulaTypesData).filter(
        (channelType) =>
            !data?.some((rowData) => rowData.message_type === channelType),
    );

    const getRowUrl = (messageType: string) =>
        routes.formulasCatalogWithTabs(FormulaCategory.Live) +
        `?message_type=${messageType}`;

    const handleChannelClick = useCallback(
        (channel: string) => {
            const draftMessagesUrl = `${routes.formulasCreate}?message_type=${channel}`;
            history.push(draftMessagesUrl);
        },
        [history],
    );

    return (
        <Container>
            <HeaderContainer>
                <Heading>{heading}</Heading>
                <ViewMessagesLink
                    to={routes.formulasCatalogWithTabs(FormulaCategory.Live)}
                >
                    View messages
                </ViewMessagesLink>
            </HeaderContainer>
            {totalMessages > 0 && (
                <MessagesProgressBar channels={calculatePercentages()} />
            )}

            <>
                <Table>
                    <thead>
                        <HeaderRow>
                            <FirstHeaderCell>Channel</FirstHeaderCell>
                            <HeaderCell>Messages</HeaderCell>
                            <LabelsHeaderCell>Alternates</LabelsHeaderCell>
                        </HeaderRow>
                    </thead>
                    <TableBody>
                        {tableData &&
                            tableData()?.map((rowData: any, index: number) => (
                                <TableRowLink
                                    key={index}
                                    to={getRowUrl(rowData?.messageType)}
                                >
                                    <FirstTableCell>
                                        {rowData?.channel}
                                    </FirstTableCell>
                                    <MessagesTableCell>
                                        {formatNumberFull(rowData?.messages)}
                                    </MessagesTableCell>
                                    <MessagesTableCell>
                                        {formatNumberFull(
                                            rowData?.uniqueMessages,
                                        )}
                                    </MessagesTableCell>
                                </TableRowLink>
                            ))}
                    </TableBody>
                </Table>
                {hasCreateFormulaAccess && (
                    <>
                        <HorizontalLine />
                        <TableFooterSection>
                            <UpperFooterComponent>
                                <ChannelsText>Use more channels</ChannelsText>
                                <CreateNewMessageButton
                                    type="button"
                                    isPrimary={false}
                                    name={'Create new message'}
                                    action={() =>
                                        history.push(
                                            routes.formulasCreate,
                                            getLocationState(),
                                        )
                                    }
                                />
                            </UpperFooterComponent>
                            <MoreChannels>
                                {activeChannelsWithZeroCount.map(
                                    (channel, index) => {
                                        const formulaType =
                                            channel as FormulaType;
                                        const formulaTypeData =
                                            FormulaTypesData[formulaType];
                                        const IconComponent =
                                            formulaTypeData.Icon as React.ElementType;
                                        return (
                                            <ChannelComponent
                                                key={index}
                                                onClick={() =>
                                                    handleChannelClick(channel)
                                                }
                                            >
                                                <IconComponent
                                                    color={
                                                        aampeTheme.colors
                                                            .textAccessibleWG01
                                                    }
                                                />
                                                <ChannelTitle>
                                                    {formulaTypeData.title}
                                                </ChannelTitle>
                                            </ChannelComponent>
                                        );
                                    },
                                )}
                            </MoreChannels>
                        </TableFooterSection>
                    </>
                )}
            </>
        </Container>
    );
};

export default LiveMessagesMain;

const Container = styled.div({
    display: 'flex',
    backgroundColor: aampeTheme.colors.white,
    padding: aampeTheme.spacing(3),
    width: '100%',
    borderRadius: aampeTheme.borderRadius.default,
    flexDirection: 'column',
    gap: aampeTheme.spacing(3),
    marginBottom: aampeTheme.spacing(2),
});

const HeaderContainer = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
});

const Heading = styled.span({
    fontSize: '24px',
    fontWeight: 500,
    lineHeight: '28px',
    color: aampeTheme.colors.textAccessibleWG02,
});

const Table = styled.table({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(3),
});

const HeaderRow = styled.tr({
    display: 'table',
    width: '100%',
    tableLayout: 'fixed',
    color: aampeTheme.colors.neutral09,
    fontSize: '16px',
    lineHeight: '20px',
    fontWeight: 500,
});
const FirstHeaderCell = styled.th({
    fontWeight: 400,
    fontSize: '16px',
    textAlign: 'left',
    paddingLeft: aampeTheme.spacing(2),
    width: '22%',
});
const LabelsHeaderCell = styled.th({
    fontWeight: 400,
    fontSize: '16px',
    textAlign: 'left',
    width: '15%',
});
const HeaderCell = styled.th({
    fontWeight: 400,
    fontSize: '16px',
    textAlign: 'left',
    width: '15%',
});

const TableRowLink = styled(Link)({
    display: 'flex',
    width: '100%',
    tableLayout: 'fixed',
    fontSize: '16px',
    fontWeight: 400,
    borderRadius: aampeTheme.borderRadius.default,
    alignItems: 'center',
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': {
        color: aampeTheme.colors.solidA01,
    },
});

const TableBody = styled.tbody({
    display: 'flex',
    flexDirection: 'column',
});
const TableCell = styled.div({
    flex: 1,
    padding: `${aampeTheme.spacing(0, 0, 3, 2)}`,
    alignItems: 'center',
    fontWeight: 400,
    fontSize: '16px',
    textAlign: 'left',
    color: 'inherit',
});
const MessagesTableCell = styled(TableCell)({});
const FirstTableCell = styled.div({
    padding: `${aampeTheme.spacing(0, 0, 3, 0)}`,
    flex: 1.5,
    alignItems: 'center',
    fontWeight: 400,
    fontSize: '16px',
    textAlign: 'left',
    color: 'inherit',
});
const ViewMessagesLink = styled(Link)({
    color: aampeTheme.colors.solidA01,
    border: 'none',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '18px',
    textDecoration: 'none',
    '&:hover': {
        textDecoration: 'underline',
    },
});
const CreateNewMessageButton = styled(BasicButton)({
    backgroundColor: 'none',
    color: aampeTheme.colors.solidA01,
    borderColor: aampeTheme.colors.solidA01,
    width: '200px',
    fontSize: '16px',
    fontWeight: 500,
});
const UpperFooterComponent = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
});

const HorizontalLine = styled.hr({
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
    margin: `${aampeTheme.spacing(0)}`,
});
const MoreChannels = styled.div({
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: aampeTheme.spacing(1),
    width: '100%',
    gap: aampeTheme.spacing(2, 5),
});
const ChannelComponent = styled.div({
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
});
const ChannelTitle = styled.div({
    fontSize: '16px',
    fontWeight: 400,
    color: aampeTheme.colors.textAccessibleWG01,
    marginLeft: aampeTheme.spacing(1),
});
const ChannelsText = styled.div({
    fontWeight: 500,
    fontSize: '20px',
    lineHeight: '22px',
    margin: aampeTheme.spacing(1, 0, 3, 0),
});
const TableFooterSection = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
});
